import {
  DataTableV2RowRenderType,
  IDataTableV2MasterColumn,
} from "../interfaces/IDataTableV2Props";

/**
 * Average character width in pixels at typical body/h6 font size.
 * This is a rough estimate — not pixel-perfect but good enough for column sizing.
 */
const CHAR_WIDTH = 8;

/** Horizontal padding inside a cell (left + right) */
const CELL_PADDING = 24;

/** Extra space for the sort icon when sorting is enabled */
const SORT_ICON_WIDTH = 22;

/** Absolute maximum auto-width for any column */
const MAX_AUTO_WIDTH = 250;

/** Minimum width for any column */
const MIN_AUTO_WIDTH = 100;

/**
 * Default widths based on column renderType.
 * These represent sensible minimums for each data presentation style.
 */
const RENDER_TYPE_WIDTHS: Partial<Record<DataTableV2RowRenderType, number>> = {
  [DataTableV2RowRenderType.TEXT]: 120,
  [DataTableV2RowRenderType.TEXT_DARK]: 120,
  [DataTableV2RowRenderType.DATE]: 130,
  [DataTableV2RowRenderType.DATE_TIME]: 175,
  [DataTableV2RowRenderType.DATE_DARK_COLOR]: 130,
  [DataTableV2RowRenderType.DATE_TIME_DARK_COLOR]: 175,
  [DataTableV2RowRenderType.CHIP_SUCCESS]: 130,
  [DataTableV2RowRenderType.CHIP_WARNING]: 130,
  [DataTableV2RowRenderType.CHIP_ERROR]: 130,
  [DataTableV2RowRenderType.AVATAR]: 70,
  [DataTableV2RowRenderType.AVATAR_TEXT_FIRST_LETTER]: 70,
  [DataTableV2RowRenderType.IMAGE]: 120,
  [DataTableV2RowRenderType.HREF_BLANK]: 160,
  [DataTableV2RowRenderType.HREF_SELF]: 160,
  [DataTableV2RowRenderType.BUTTON_TEXT]: 140,
  [DataTableV2RowRenderType.BUTTON_CONTAINED]: 150,
  [DataTableV2RowRenderType.BUTTON_OUTLINED]: 150,
  [DataTableV2RowRenderType.DESCRIPTION]: 200,
  [DataTableV2RowRenderType.CUSTOM_RENDER]: 150,
};

/**
 * Default widths based on the column's `type` field (data type hint).
 */
const DATA_TYPE_WIDTHS: Record<string, number> = {
  text: 130,
  number: 110,
  amount: 140,
  currency: 140,
  date: 130,
  date_range: 180,
  phone: 145,
  select: 140,
  multi_select: 180,
};

/**
 * Estimates the ideal initial width for a column based on:
 * 1. Header text length (ensures headers never truncate)
 * 2. Render type defaults (dates, chips, buttons get appropriate widths)
 * 3. Data type defaults (phone, currency, etc.)
 * 4. Sample of row data content lengths
 *
 * @param column - The master column definition
 * @param rows - The data rows (only first few are sampled)
 * @param sampleSize - Number of rows to sample for content width (default: 5)
 * @returns Estimated width in pixels
 */
export const estimateColumnWidth = (
  column: IDataTableV2MasterColumn,
  rows: any[] = [],
  sampleSize: number = 5
): number => {
  // 1. Header text floor — header should never be truncated
  const headerText = column.headerName || "";
  const sortExtra = column.enableSorting ? SORT_ICON_WIDTH : 0;
  const headerWidth = headerText.length * CHAR_WIDTH + CELL_PADDING + sortExtra;

  // 2. Render type default
  const renderTypeWidth = RENDER_TYPE_WIDTHS[column.renderType] || MIN_AUTO_WIDTH;

  // 3. Data type default (if column.type is set)
  const dataTypeWidth = column.type ? DATA_TYPE_WIDTHS[column.type] || MIN_AUTO_WIDTH : MIN_AUTO_WIDTH;

  // 4. Content width from sample rows
  let maxContentWidth = 0;
  const sample = rows.slice(0, sampleSize);
  for (const row of sample) {
    const value = row[column.fieldName];
    if (value !== null && value !== undefined && value !== "") {
      let strLen: number;
      if (column.turncateLength) {
        // If column truncates content, use truncate length as max
        strLen = Math.min(String(value).length, column.turncateLength);
      } else {
        strLen = String(value).length;
      }
      const contentWidth = strLen * CHAR_WIDTH + CELL_PADDING;
      maxContentWidth = Math.max(maxContentWidth, contentWidth);
    }
  }

  // Final: max of all estimates, clamped between MIN and MAX
  const estimated = Math.max(headerWidth, renderTypeWidth, dataTypeWidth, maxContentWidth);
  return Math.min(Math.max(estimated, MIN_AUTO_WIDTH), MAX_AUTO_WIDTH);
};

/**
 * Computes estimated widths for all columns.
 * Returns a map of columnKey → width in pixels.
 * Skips columns that already have an explicit `width` set.
 */
export const computeColumnWidths = (
  columns: IDataTableV2MasterColumn[],
  rows: any[] = [],
  sampleSize: number = 5
): Record<string, number> => {
  const widths: Record<string, number> = {};

  for (const column of columns) {
    // Skip undefined/null columns (can happen during visibility reorder)
    if (!column) continue;
    // If column already has explicit width, skip auto-estimation
    if (column.width) {
      continue;
    }
    widths[column.key] = estimateColumnWidth(column, rows, sampleSize);
  }

  return widths;
};
